// client/src/screens/admin/products/haberdashery/HaberdasheryForm.shared.jsx

import React, { memo } from "react";

import { normalizeProductImage } from "../../../../utils/productImages";

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

export const SHOP_KEY = "haberdashery";

export const DRAFT_SCHEMA_VERSION = 5;
export const DRAFT_TTL_MS = 1000 * 60 * 60 * 24 * 14;
export const DRAFT_WRITE_DELAY_MS = 400;

export const FLAG_FIELDS = [
  "exclusive",
  "limitedEdition",
  "isVintage",
  "isCustomMade",
  "isNewArrival",
  "isEditorsPick",
  "isTrending",
  "isFeatured",
];

export const HABERDASHERY_UNIT_OPTIONS = [
  { value: "piece", label: "Piece", sellMode: "unit" },
  { value: "metre", label: "Metre", sellMode: "length" },
  { value: "half", label: "Half metre", sellMode: "length" },
  { value: "cut", label: "Cut", sellMode: "length" },
  { value: "pair", label: "Pair", sellMode: "unit" },
  { value: "pack", label: "Pack", sellMode: "unit" },
  { value: "card", label: "Card", sellMode: "unit" },
  { value: "set", label: "Set", sellMode: "unit" },
  { value: "roll", label: "Roll", sellMode: "unit" },
];

export const HABERDASHERY_UNIT_TYPES = new Set(
  HABERDASHERY_UNIT_OPTIONS.map((item) => item.value),
);

export const HABERDASHERY_LENGTH_TYPES = new Set(["metre", "half", "cut"]);

export const DEFAULT_UNIT_TYPE = "piece";

/* -------------------------------------------------------------------------- */
/*                                  UI atoms                                  */
/* -------------------------------------------------------------------------- */

export const Field = memo(function Field({
  label = "",
  children,
  hint = "",
  error = "",
  className = "",
}) {
  return (
    <label className={`admin-field ${className}`.trim()}>
      {label ? <span className="admin-field__label">{label}</span> : null}

      {children}

      {hint ? <span className="admin-field__hint">{hint}</span> : null}

      {error ? <span className="admin-field__error">{error}</span> : null}
    </label>
  );
});

export const Section = memo(function Section({
  title = "",
  hint = "",
  children,
  className = "",
}) {
  return (
    <section className={`admin-form-section ${className}`.trim()}>
      {title || hint ? (
        <div className="admin-form-section__header">
          {title ? (
            <h3 className="admin-form-section__title">{title}</h3>
          ) : null}

          {hint ? <p className="admin-form-section__hint">{hint}</p> : null}
        </div>
      ) : null}

      {children}
    </section>
  );
});

/* -------------------------------------------------------------------------- */
/*                              Primitive helpers                             */
/* -------------------------------------------------------------------------- */

export function safeText(value = "") {
  return String(value ?? "").trim();
}

export function normalizeLower(value = "") {
  return safeText(value).toLowerCase();
}

export function normalizeSku(value = "") {
  return safeText(value).toUpperCase();
}

export function getIdValue(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value?._id || value?.id || "";
}

export function getProductId(product = null) {
  return getIdValue(product);
}

export function makeClientId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function splitTokens(value = "") {
  return [
    ...new Set(
      String(value || "")
        .split(/[\n,]+/)
        .map((item) => normalizeLower(item))
        .filter(Boolean),
    ),
  ];
}

export function joinTokens(values = [], fallback = "") {
  if (Array.isArray(values) && values.length) {
    return values.map(safeText).filter(Boolean).join(", ");
  }

  return safeText(fallback);
}

export function toNumberOrNull(value) {
  if (value === "" || value === null || value === undefined) return null;

  const num = Number(value);
  return Number.isFinite(num) && num >= 0 ? num : null;
}

export function toNumberOrZero(value) {
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 ? num : 0;
}

export function toIntegerOrZero(value) {
  const num = Math.floor(Number(value));
  return Number.isFinite(num) && num >= 0 ? num : 0;
}

export function hasPositiveNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) && num > 0;
}

/* -------------------------------------------------------------------------- */
/*                            Haberdashery helpers                            */
/* -------------------------------------------------------------------------- */

export function normalizeUnitType(value = "") {
  const next = normalizeLower(value);
  return HABERDASHERY_UNIT_TYPES.has(next) ? next : DEFAULT_UNIT_TYPE;
}

export function isLengthType(unitType = "") {
  return HABERDASHERY_LENGTH_TYPES.has(normalizeUnitType(unitType));
}

export function getUnitMeta(unitType = "") {
  const normalized = normalizeUnitType(unitType);

  return (
    HABERDASHERY_UNIT_OPTIONS.find((item) => item.value === normalized) ||
    HABERDASHERY_UNIT_OPTIONS[0]
  );
}

export function getDefaultUnitValue(unitType = "") {
  switch (normalizeUnitType(unitType)) {
    case "half":
      return 0.5;
    case "metre":
    case "cut":
    case "piece":
    case "pair":
    case "pack":
    case "card":
    case "set":
    case "roll":
    default:
      return 1;
  }
}

/* -------------------------------------------------------------------------- */
/*                                  Images                                    */
/* -------------------------------------------------------------------------- */

export function normalizeImage(image) {
  return normalizeProductImage(image);
}

export function normalizeImageArray(images = []) {
  const raw = Array.isArray(images) ? images : images ? [images] : [];
  return raw.map(normalizeImage).filter(Boolean);
}

/* -------------------------------------------------------------------------- */
/*                                  State                                     */
/* -------------------------------------------------------------------------- */

export function buildFlagState(product = null) {
  return FLAG_FIELDS.reduce((acc, key) => {
    acc[key] = Boolean(product?.[key]);
    return acc;
  }, {});
}

export function buildVariantRow(variant = {}) {
  const variantType = normalizeUnitType(variant?.variantType || variant?.type);

  return {
    _clientId: variant?._clientId || makeClientId(),

    name: safeText(variant?.name),
    shortDescription: safeText(variant?.shortDescription),
    sku: normalizeSku(variant?.sku),

    variantType,

    unitValue:
      variant?.unitValue === undefined || variant?.unitValue === null
        ? String(getDefaultUnitValue(variantType))
        : String(variant.unitValue),

    unitLabel: safeText(variant?.unitLabel),

    price:
      variant?.price === undefined || variant?.price === null
        ? ""
        : String(variant.price),

    countInStock:
      variant?.countInStock === undefined || variant?.countInStock === null
        ? ""
        : String(variant.countInStock),

    countReserved:
      variant?.countReserved === undefined || variant?.countReserved === null
        ? "0"
        : String(variant.countReserved),

    lengthInStock:
      variant?.lengthInStock === undefined || variant?.lengthInStock === null
        ? ""
        : String(variant.lengthInStock),

    lengthReserved:
      variant?.lengthReserved === undefined || variant?.lengthReserved === null
        ? "0"
        : String(variant.lengthReserved),

    itemsPerPack:
      variant?.itemsPerPack === undefined || variant?.itemsPerPack === null
        ? "1"
        : String(variant.itemsPerPack),

    color: safeText(variant?.color),
    size: safeText(variant?.size),
    material: safeText(variant?.material),
    length: safeText(variant?.length),

    images: normalizeImageArray(variant?.images ?? variant?.image),
  };
}

export function mergeById(items = []) {
  const map = new Map();

  for (const item of items) {
    const id = getIdValue(item);
    if (id) map.set(id, item);
  }

  return [...map.values()];
}

export function buildInitialState(product = null, draft = null) {
  const source = draft || product || {};
  const haberdashery = source.haberdashery || product?.haberdashery || {};
  const madeToMeasure = source.madeToMeasure || product?.madeToMeasure || {};

  const productVariants =
    product?.isVariant && Array.isArray(product?.variants)
      ? product.variants
      : [];

  const draftVariants =
    Array.isArray(draft?.variants) && draft.variants.length
      ? draft.variants
      : null;

  const unitType = normalizeUnitType(
    haberdashery.unitType || DEFAULT_UNIT_TYPE,
  );

  return {
    category: getIdValue(source.category || product?.category),
    subcategory: getIdValue(
      source.subCategory ||
        source.subcategory ||
        product?.subCategory ||
        product?.subcategory,
    ),

    name: safeText(source.name),
    brand: safeText(source.brand),
    description: safeText(source.description),

    sku: normalizeSku(source.sku || product?.sku),

    isVariant: Boolean(source.isVariant),

    price:
      source.price === undefined || source.price === null
        ? product?.isVariant
          ? ""
          : (product?.price ?? "")
        : String(source.price),

    countInStock:
      source.countInStock === undefined || source.countInStock === null
        ? product?.isVariant
          ? ""
          : (product?.countInStock ?? "")
        : String(source.countInStock),

    countReserved:
      source.countReserved === undefined || source.countReserved === null
        ? product?.isVariant
          ? "0"
          : (product?.countReserved ?? "0")
        : String(source.countReserved),

    unitType,
    unitValue:
      haberdashery.unitValue === undefined || haberdashery.unitValue === null
        ? String(getDefaultUnitValue(unitType))
        : String(haberdashery.unitValue),

    unitLabel: safeText(source.unitLabel || product?.unitLabel),
    pricingUnit: safeText(source.pricingUnit || product?.pricingUnit),
    includedQuantity:
      source.includedQuantity === undefined || source.includedQuantity === null
        ? product?.includedQuantity === undefined ||
          product?.includedQuantity === null
          ? ""
          : String(product.includedQuantity)
        : String(source.includedQuantity),
    includedItemLabel: safeText(
      source.includedItemLabel || product?.includedItemLabel,
    ),

    lengthInStock:
      haberdashery.lengthInStock === undefined ||
      haberdashery.lengthInStock === null
        ? ""
        : String(haberdashery.lengthInStock),

    lengthReserved:
      haberdashery.lengthReserved === undefined ||
      haberdashery.lengthReserved === null
        ? "0"
        : String(haberdashery.lengthReserved),

    itemsPerPack:
      source.itemsPerPack === undefined || source.itemsPerPack === null
        ? "1"
        : String(source.itemsPerPack),

    colorsText:
      source.colorsText ||
      joinTokens(source.colors, source.color) ||
      joinTokens(product?.colors, product?.color),

    sizesText:
      source.sizesText ||
      joinTokens(source.sizes, source.size) ||
      joinTokens(product?.sizes, product?.size),

    materialsText:
      source.materialsText ||
      joinTokens(source.materials, source.material) ||
      joinTokens(product?.materials, product?.material),

    patternsText:
      source.patternsText ||
      joinTokens(source.patterns) ||
      joinTokens(product?.patterns),

    tagsText:
      source.tagsText || joinTokens(source.tags) || joinTokens(product?.tags),

    groupsText:
      source.groupsText ||
      joinTokens(source.groups) ||
      joinTokens(product?.groups),

    origin: safeText(source.origin || product?.origin),
    usage: safeText(source.usage || product?.usage),
    careInstructions: safeText(
      source.careInstructions || product?.careInstructions,
    ),

    finish: safeText(haberdashery.finish),

    widthMm:
      haberdashery.widthMm === undefined || haberdashery.widthMm === null
        ? ""
        : String(haberdashery.widthMm),

    diameterMm:
      haberdashery.diameterMm === undefined || haberdashery.diameterMm === null
        ? ""
        : String(haberdashery.diameterMm),

    compatibility: safeText(haberdashery.compatibility),

    compatibilitiesText:
      source.compatibilitiesText ||
      joinTokens(haberdashery.compatibilities) ||
      safeText(haberdashery.compatibility),

    madeToMeasureEligible: Boolean(madeToMeasure.eligibleAsHaberdashery),
    madeToMeasureSuitabilityNotes: safeText(madeToMeasure.suitabilityNotes),

    images: normalizeImageArray(source.images ?? product?.images),

    ...buildFlagState(source || product),

    variants: draftVariants
      ? draftVariants.map(buildVariantRow)
      : productVariants.length
        ? productVariants.map(buildVariantRow)
        : [buildVariantRow()],
  };
}

/* -------------------------------------------------------------------------- */
/*                               Draft storage                                */
/* -------------------------------------------------------------------------- */

export function getDraftKey(product = null) {
  return `haberdashery-product-form:${getProductId(product) || "new"}`;
}

export function canUseStorage() {
  return typeof window !== "undefined" && Boolean(window.localStorage);
}

export function readDraft(key) {
  if (!canUseStorage()) return null;

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (parsed?.schemaVersion !== DRAFT_SCHEMA_VERSION) return null;

    if (!parsed?.savedAt || Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
      window.localStorage.removeItem(key);
      return null;
    }

    return parsed.data || null;
  } catch {
    return null;
  }
}

export function writeDraft(key, value) {
  if (!canUseStorage()) return;

  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({
        schemaVersion: DRAFT_SCHEMA_VERSION,
        savedAt: Date.now(),
        data: value,
      }),
    );
  } catch {
    // Ignore quota/security errors.
  }
}

export function clearDraft(key) {
  if (!canUseStorage()) return;

  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage errors.
  }
}

/* -------------------------------------------------------------------------- */
/*                                Validation                                  */
/* -------------------------------------------------------------------------- */

export function validateForm(form) {
  const category = safeText(form.category);
  const name = safeText(form.name);
  const description = safeText(form.description);
  const sku = normalizeSku(form.sku);
  const unitType = normalizeUnitType(form.unitType);
  const lengthBased = isLengthType(unitType);

  if (!category) {
    return "Please select a category before saving this haberdashery product.";
  }

  if (!name) return "Product name is required.";
  if (!description) return "Product description is required.";

  const widthMm = toNumberOrNull(form.widthMm);
  const diameterMm = toNumberOrNull(form.diameterMm);

  if (form.widthMm !== "" && widthMm === null) {
    return "Width must be a valid non-negative number.";
  }

  if (form.diameterMm !== "" && diameterMm === null) {
    return "Diameter must be a valid non-negative number.";
  }

  if (form.includedQuantity !== "") {
    const includedQuantity = Number(form.includedQuantity);

    if (
      !Number.isFinite(includedQuantity) ||
      includedQuantity < 0 ||
      !Number.isInteger(includedQuantity)
    ) {
      return "Included quantity must be a whole number.";
    }
  }

  if (!form.isVariant) {
    if (!sku) return "SKU is required for simple haberdashery products.";

    if (!hasPositiveNumber(form.price)) {
      return "Price must be greater than 0.";
    }

    if (!hasPositiveNumber(form.unitValue)) {
      return "Unit value must be greater than 0.";
    }

    if (lengthBased) {
      if (
        toNumberOrZero(form.lengthReserved) > toNumberOrZero(form.lengthInStock)
      ) {
        return "Length reserved cannot exceed length in stock.";
      }

      return "";
    }

    if (
      toIntegerOrZero(form.countReserved) > toIntegerOrZero(form.countInStock)
    ) {
      return "Reserved stock cannot exceed stock.";
    }

    return "";
  }

  const variants = Array.isArray(form.variants) ? form.variants : [];

  if (!variants.length) return "Add at least one variant.";

  const skuSet = new Set();

  for (const variant of variants) {
    const variantSku = normalizeSku(variant.sku);
    const variantType = normalizeUnitType(variant.variantType);
    const variantLengthBased = isLengthType(variantType);

    if (!variantSku) return "Every variant must have a SKU.";

    if (skuSet.has(variantSku)) {
      return "Variant SKUs must be unique within this product.";
    }

    skuSet.add(variantSku);

    if (!hasPositiveNumber(variant.price)) {
      return `Price is required for variant ${variantSku}.`;
    }

    if (!hasPositiveNumber(variant.unitValue)) {
      return `Unit value must be greater than 0 for variant ${variantSku}.`;
    }

    if (variantLengthBased) {
      if (
        toNumberOrZero(variant.lengthReserved) >
        toNumberOrZero(variant.lengthInStock)
      ) {
        return `Length reserved cannot exceed length in stock for variant ${variantSku}.`;
      }

      continue;
    }

    if (
      toIntegerOrZero(variant.countReserved) >
      toIntegerOrZero(variant.countInStock)
    ) {
      return `Reserved stock cannot exceed stock for variant ${variantSku}.`;
    }
  }

  return "";
}

/* -------------------------------------------------------------------------- */
/*                                  Payload                                   */
/* -------------------------------------------------------------------------- */

export function buildPayload(form) {
  const colors = splitTokens(form.colorsText);
  const sizes = splitTokens(form.sizesText);
  const materials = splitTokens(form.materialsText);
  const patterns = splitTokens(form.patternsText);
  const tags = splitTokens(form.tagsText);
  const groups = splitTokens(form.groupsText);
  const compatibilities = splitTokens(form.compatibilitiesText);

  const unitType = normalizeUnitType(form.unitType);
  const lengthBased = isLengthType(unitType);

  const flags = FLAG_FIELDS.reduce((acc, key) => {
    acc[key] = Boolean(form[key]);
    return acc;
  }, {});

  const payload = {
    shopKey: SHOP_KEY,

    category: safeText(form.category),
    subCategory: safeText(form.subcategory) || null,

    name: safeText(form.name),
    brand: safeText(form.brand),
    description: safeText(form.description),

    isVariant: Boolean(form.isVariant),

    images: normalizeImageArray(form.images),

    color: colors[0] || "",
    colors,

    size: sizes[0] || "",
    sizes,

    material: materials[0] || "",
    materials,

    pattern: patterns[0] || "",
    patterns,

    tags,
    groups,

    origin: safeText(form.origin),
    usage: safeText(form.usage),
    careInstructions: safeText(form.careInstructions),

    ...flags,

    madeToMeasure: {
      eligibleAsDesign: false,
      eligibleAsTextile: false,
      eligibleAsHaberdashery: Boolean(form.madeToMeasureEligible),
      suitabilityNotes: safeText(form.madeToMeasureSuitabilityNotes),
    },

    pricingUnit: safeText(form.pricingUnit),
    includedQuantity:
      form.includedQuantity === "" ? null : toIntegerOrZero(form.includedQuantity),
    includedItemLabel: safeText(form.includedItemLabel),

    haberdashery: {
      finish: normalizeLower(form.finish),
      widthMm: toNumberOrNull(form.widthMm),
      diameterMm: toNumberOrNull(form.diameterMm),
      compatibility: normalizeLower(form.compatibility),
      compatibilities,

      sellMode: form.isVariant ? "variant" : lengthBased ? "length" : "unit",
      unitType: form.isVariant ? DEFAULT_UNIT_TYPE : unitType,
      unitValue: form.isVariant ? 1 : toNumberOrZero(form.unitValue),

      lengthInStock:
        !form.isVariant && lengthBased ? toNumberOrZero(form.lengthInStock) : 0,

      lengthReserved:
        !form.isVariant && lengthBased
          ? toNumberOrZero(form.lengthReserved)
          : 0,
    },
  };

  if (form.isVariant) {
    payload.sku = undefined;
    payload.price = 0;
    payload.countInStock = 0;
    payload.countReserved = 0;
    payload.itemsPerPack = 1;
    payload.unitLabel = "";

    payload.variants = form.variants.map((variant) => {
      const variantType = normalizeUnitType(variant.variantType);
      const variantLengthBased = isLengthType(variantType);

      return {
        name: safeText(variant.name),
        shortDescription: safeText(variant.shortDescription),
        sku: normalizeSku(variant.sku),
        variantType,

        price: toNumberOrZero(variant.price),

        unitValue: toNumberOrZero(variant.unitValue),
        unitLabel: safeText(variant.unitLabel),

        itemsPerPack: variantLengthBased
          ? 1
          : Math.max(1, toIntegerOrZero(variant.itemsPerPack) || 1),

        countInStock: variantLengthBased
          ? 0
          : toIntegerOrZero(variant.countInStock),

        countReserved: variantLengthBased
          ? 0
          : toIntegerOrZero(variant.countReserved),

        lengthInStock: variantLengthBased
          ? toNumberOrZero(variant.lengthInStock)
          : 0,

        lengthReserved: variantLengthBased
          ? toNumberOrZero(variant.lengthReserved)
          : 0,

        color: normalizeLower(variant.color),
        size: normalizeLower(variant.size),
        material: normalizeLower(variant.material),
        length: normalizeLower(variant.length),

        images: normalizeImageArray(variant.images),
      };
    });

    return payload;
  }

  payload.sku = normalizeSku(form.sku);
  payload.price = toNumberOrZero(form.price);
  payload.unitLabel = safeText(form.unitLabel);
  payload.itemsPerPack = Math.max(1, toIntegerOrZero(form.itemsPerPack) || 1);

  payload.countInStock = lengthBased ? 0 : toIntegerOrZero(form.countInStock);
  payload.countReserved = lengthBased ? 0 : toIntegerOrZero(form.countReserved);
  payload.variants = [];

  return payload;
}
